import {
    AttributeDropdown,
    getNotChosenOption,
    getSystemAccessTemplateOptions,
} from '@Component/AttributeDropdown/AttributeDropdown';
import { BodyDTO, EmployeeDTO } from '@Models/model.types';
import { APIError, ListResponse, showActionLogMessage } from '@tlx/astro-shared';
import {
    Alert,
    AlertContent,
    Button,
    ContentSwitch,
    ContentSwitcher,
    DateField,
    Form,
    Group,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalSubtitle,
    ModalTitle,
} from '@tlx/atlas';
import { SpinnerButton } from '@tlx/tsk-shared';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { mapValidationMessages } from '@Util/apiErrorUtils';
import { dateUtil } from '@Util/date';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown';
import { APIUrls, DropdownUrls } from '@Util/urls';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

type AssignEntitlementsContentSwitcher = 'TEMPLATE' | 'USER' | 'LOGIN_ACCESS';
export function AssignEntitlementsModal({
    open,
    onClose,
    employeeIds,
    mutate,
    setValidationsMap,
}: {
    open: boolean;
    onClose: () => void;
    employeeIds: number[];
    mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
}) {
    const formId = 'AssignEntitlementsForm';
    const intl = useIntl();
    const [awaitingResponse, setAwaitingResponse] = useState(false);

    const [contentSwitch, setContentSwitch] = useState<AssignEntitlementsContentSwitcher>('TEMPLATE');
    const [allowLogin, setAllowLogin] = useState('');
    const unexpectedErrorMessage = intl.formatMessage({
        id: 'validation_unexpected_error',
    });
    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);
    if (!open) {
        return null;
    }

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const formDate = new FormData(event.currentTarget);
        const data = Object.fromEntries(formDate.entries());
        setValidationsMap(new Map());

        // Create initial params with all potential values
        const initialParams = {
            endDate: data.endDate as string,
            roleTemplateId: data.templateDropdown as string,
            employeeTemplateId: data.userDropdown as string,
            allowLogin: data.allowLogin as string,
            ignoreAdvice: (errorsToShowInModal.length > 0).toString(),
        };

        // Filter out empty values
        const params = (Object.keys(initialParams) as (keyof typeof initialParams)[]).reduce(
            (acc, key) => {
                // Only add the key if its value exists and is not an empty string
                if (initialParams[key] && initialParams[key].trim() !== '') {
                    acc[key] = initialParams[key];
                }
                return acc;
            },
            {} as Record<string, string>,
        );
        const url = computeUrl(APIUrls.ASSIGN_ENTITLEMENTS, mapObjectToUrlSearchParams(params));
        const bodyDTO: BodyDTO = {
            employeeIds: employeeIds.join(','),
        };
        setAwaitingResponse(true);
        const { response, error } = await callApi(url, MethodType.POST, JSON.stringify(bodyDTO));
        setAwaitingResponse(false);
        if (response) {
            mutate();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message:
                    intl.formatMessage({
                        id: 'text_action_completed',
                    }) +
                    ' ' +
                    intl.formatMessage({ id: 'text_template_privileges' }),
            });
            onClose();
        }
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    setValidationsMap,
                    setErrorsToShowInModal,
                    unexpectedErrorMessage,
                    onClose,
                );
            }
        }
    };

    return (
        <Modal
            className="tsk-width-500"
            open={open}
            onClose={() => {
                if (!awaitingResponse) {
                    onClose();
                }
            }}
        >
            <ModalLayout>
                {!awaitingResponse && <ModalCloseButton />}
                <ModalTitle>
                    <FormattedMessage id={'text_template_privileges'} />
                </ModalTitle>
                <ModalSubtitle>
                    <FormattedMessage id={'text_assign_privileges_short'} />
                </ModalSubtitle>
                <ModalContent>
                    <ContentSwitcher
                        data-testid={'assign-entitlements-content-switcher'}
                        onChange={(event) => {
                            if (event.target instanceof HTMLInputElement) {
                                setContentSwitch(event.target.value as AssignEntitlementsContentSwitcher);
                                setErrorsToShowInModal([]);
                            }
                        }}
                    >
                        <ContentSwitch
                            name="content"
                            value={'TEMPLATE'}
                            data-testid={''}
                            defaultChecked={contentSwitch === 'TEMPLATE'}
                        >
                            <FormattedMessage id="text_template_privileges" />
                        </ContentSwitch>
                        <ContentSwitch
                            name="content"
                            value={'USER'}
                            data-testid={''}
                            defaultChecked={contentSwitch === 'USER'}
                        >
                            <FormattedMessage id="text_user_is_admin" />
                        </ContentSwitch>
                        <ContentSwitch
                            name="content"
                            value={'LOGIN_ACCESS'}
                            data-testid={''}
                            defaultChecked={contentSwitch === 'LOGIN_ACCESS'}
                        >
                            <FormattedMessage id="text_login_access" />
                        </ContentSwitch>
                    </ContentSwitcher>
                    <Form className="atl-flex atl-flex-col atl-mt-16" onSubmit={onSubmit} id={formId}>
                        {contentSwitch === 'TEMPLATE' && (
                            <AttributeDropdown
                                id={'templateDropdown'}
                                url={DropdownUrls.ENTITLEMENTS_TEMPLATES}
                                options={getNotChosenOption(intl)}
                                onChange={() => setErrorsToShowInModal([])}
                            />
                        )}
                        {contentSwitch === 'USER' && (
                            <AttributeDropdown
                                id={'userDropdown'}
                                url={DropdownUrls.EMPLOYEES}
                                options={getNotChosenOption(intl)}
                                onChange={() => setErrorsToShowInModal([])}
                            />
                        )}
                        {contentSwitch === 'LOGIN_ACCESS' && (
                            <Group className="atl-flex atl-gap-16">
                                <AttributeDropdown
                                    id={'allowLogin'}
                                    options={[...getNotChosenOption(intl), ...getSystemAccessTemplateOptions(intl)]}
                                    onChange={(val) => {
                                        setAllowLogin(val);
                                        setErrorsToShowInModal([]);
                                    }}
                                    text="text_login_access"
                                />
                                <DateField
                                    data-testid="end-date"
                                    name="endDate"
                                    isDisabled={
                                        !getSystemAccessTemplateOptions(intl).some((option) => option.id === allowLogin) // Disable if selection is not in the system access options
                                    }
                                    label={`${intl.formatMessage({ id: 'text_date' })} ${intl.formatMessage({ id: 'text_to_and_including_short_lc' })}`}
                                    validate={(value) => dateUtil.validateDateString(value, intl)}
                                />
                            </Group>
                        )}
                    </Form>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-flex-col'}>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="info" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <div className="atl-flex atl-gap-8">
                        <SpinnerButton
                            className="atl-button--primary"
                            data-testid="change-employee-info-run-button"
                            type={'submit'}
                            form={formId}
                            disabled={awaitingResponse}
                            isLoading={awaitingResponse}
                        >
                            <FormattedMessage id={errorsToShowInModal.length ? 'text_ok' : 'button_save'} />
                        </SpinnerButton>
                        <Button
                            variant={'secondary'}
                            onClick={onClose}
                            data-testid="change-employee-info-modal-cancel-button"
                            disabled={awaitingResponse}
                        >
                            <FormattedMessage id={'text_cancel'} />
                        </Button>
                    </div>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
